import React, { useId, useRef, useState } from 'react';
import type {
  ConversionRow,
  LeadSourceRow,
  LeadSourceInsights,
  ReferrerRow,
  SettlementsByMonth,
} from '../../../hooks/pipelineTracker/useLeadSourceReport';
import { CalendarRange, Layers, Percent, UserCheck } from 'lucide-react';
import { Panel, Pill, Honesty, money, num } from './pipelineParts';
import { CHART_PALETTE, chartColour } from './trackerKit';
import { BreakdownCard, FloatingTooltip, anchorOf, type BreakdownRow, type TooltipAnchor } from './chartTooltip';

/**
 * Leads & Partners, V6 sections: source mix, conversion, settlements by month, referrers.
 *
 * NOTHING HERE COMPUTES A FIGURE THE SERVER DID NOT. Every count, cent and ratio arrives from
 * reports/leadSourceInsights.ts, which folds them from the same rows as the live table. The only
 * arithmetic below is presentation: a share of a column for a bar width, and a basis-point figure
 * printed as a percentage.
 */

const NOT_RECORDED = 'Not recorded';
const NOT_RECORDED_COLOUR = '#6b7280';

/**
 * The lead-source palette: fifteen hues no two of which read as one colour on the dark ground.
 *
 * Not CHART_PALETTE by modulo. That has ten hues, so the eleventh source in name order wrapped back
 * to the first: on a real eleven-source book "Client referral" and "X imported" were the same blue,
 * and its sky sat beside its cyan, so "Online advertising" and "Traditional advertising" read as one
 * (founder, 5 Oct 2026). This keeps the house palette's first eight hues (sky dropped; fuchsia gives
 * way to a light pink) and adds seven more, each chosen to stay at least 15 CIEDE2000 from every
 * other entry and from the "Not recorded" grey. For scale, sky against cyan is 12.3. The test
 * proves the minimum, so a new hue that drifts close to an old one fails CI.
 */
export const SOURCE_PALETTE: ReadonlyArray<string> = [
  ...CHART_PALETTE.slice(0, 8), // blue, amber, emerald, violet, rose, cyan, lime, orange
  '#f0abfc', // light fuchsia
  '#fde047', // yellow
  '#fca5a5', // light red
  '#a5b4fc', // periwinkle
  '#5eead4', // aqua
  '#0d9488', // deep teal
  '#f472b6', // pink
];

/**
 * A colour for the n-th source past the palette. A book with more sources than SOURCE_PALETTE has
 * hues cannot keep every pair far apart, but it never repeats one: golden-angle hues, alternating
 * light and mid tones, so neighbours in name order still differ.
 */
const overflowColour = (n: number): string => {
  const h = (n * 137.508 + 20) % 360;
  const s = 0.7;
  const l = n % 2 === 0 ? 0.72 : 0.55;
  // HSL to #rrggbb: segmentFill appends a hex alpha, so the colour must be hex.
  const a = s * Math.min(l, 1 - l);
  const channel = (k: number) => {
    const c = l - a * Math.max(-1, Math.min((k + h / 30) % 12 - 3, 9 - ((k + h / 30) % 12), 1));
    return Math.round(c * 255).toString(16).padStart(2, '0');
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`;
};

/**
 * One colour per source for the whole tab, so a source is the same colour in the funnel mix and in
 * the month chart. Assigned by name order, not by rank: a rank changes with every filter, and a
 * source that turned from blue to amber when the broker select moved would read as a different
 * source. "Not recorded" is always grey because it is a data gap, not a source. Every source gets
 * its own colour; none is ever shared.
 */
export function sourceColours(names: Iterable<string>): Map<string, string> {
  const unique = [...new Set(names)].filter(name => name !== NOT_RECORDED)
    .sort((a, b) => a.localeCompare(b, 'en-AU'));
  const out = new Map<string, string>(unique.map((name, i) => [
    name,
    i < SOURCE_PALETTE.length ? SOURCE_PALETTE[i] : overflowColour(i - SOURCE_PALETTE.length),
  ]));
  out.set(NOT_RECORDED, NOT_RECORDED_COLOUR);
  return out;
}

/** A source's segment fill: its colour deepening toward the bottom, the house gradient on a bar. */
export const segmentFill = (colour: string): string => `linear-gradient(180deg, ${colour}, ${colour}b3)`;

export const bpPercent = (bp: number | null | undefined): string =>
  bp == null ? '-' : `${(bp / 100).toLocaleString('en-AU', { maximumFractionDigits: 1 })}%`;

/** Share of a column total, whole percent, or '-' when the column is empty. */
export const shareOf = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '-';

const Legend: React.FC<{ names: string[]; colours: Map<string, string> }> = ({ names, colours }) => (
  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-400">
    {names.map(name => (
      <span key={name} className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: segmentFill(colours.get(name) ?? NOT_RECORDED_COLOUR) }} />
        {name}
      </span>
    ))}
  </div>
);

type Mode = 'value' | 'count';

const ModeToggle: React.FC<{ mode: Mode; onChange: (m: Mode) => void; label: string }> = ({ mode, onChange, label }) => (
  <div className="inline-flex rounded-md border border-gray-700 bg-gray-900/60 p-0.5" role="group" aria-label={label}>
    {(['value', 'count'] as const).map(m => (
      <button key={m} type="button" aria-pressed={mode === m} onClick={() => onChange(m)}
        className={`rounded px-2.5 py-1 text-xs font-bold ${mode === m ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
        {m === 'value' ? '$' : '#'}
      </button>
    ))}
  </div>
);

/**
 * Each source's share of leads, of lodged leads and of settled leads, one stacked bar per point.
 *
 * Three points, not the mockup's Submissions: the export dates lodgement, and "lodged" is the
 * milestone the table's column and the cohort band already call it, so the chart uses the same word
 * for the same deals. Share by count or by loan amount, the reader's choice.
 */
export const SourceMixPanel: React.FC<{ rows: LeadSourceRow[]; colours: Map<string, string> }> = ({ rows, colours }) => {
  const [mode, setMode] = useState<Mode>('count');
  const [hover, setHover] = useState<{ point: string; key: string; anchor: TooltipAnchor } | null>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const points: Array<{ label: string; of: (r: LeadSourceRow) => number }> = mode === 'count'
    ? [
      { label: 'Leads', of: r => r.deals },
      { label: 'Lodged', of: r => r.lodged ?? 0 },
      { label: 'Settled', of: r => r.settled },
    ]
    : [
      { label: 'Leads', of: r => r.loanAmountCents },
      { label: 'Lodged', of: r => r.lodgedLoanAmountCents ?? 0 },
      { label: 'Settled', of: r => r.settledLoanAmountCents },
    ];
  const fmt = (v: number) => (mode === 'count' ? num(v) : money(v));
  const totalText = (total: number) => (mode === 'count' ? `${num(total)} deals` : money(total));
  const hovered = hover ? points.find(p => p.label === hover.point) : undefined;
  const hoveredTotal = hovered ? rows.reduce((sum, r) => sum + Math.max(0, hovered.of(r)), 0) : 0;
  const breakdown: BreakdownRow[] = hovered
    ? rows.filter(r => hovered.of(r) > 0).map(r => ({
      key: r.leadSource,
      name: r.leadSource,
      colour: colours.get(r.leadSource) ?? NOT_RECORDED_COLOUR,
      value: fmt(hovered.of(r)),
      share: shareOf(hovered.of(r), hoveredTotal),
    }))
    : [];
  return (
    <Panel title="Source mix across the funnel" icon={Layers} accent={chartColour(3)} badge={<ModeToggle mode={mode} onChange={setMode} label="Source mix measure" />}
      blurb="Each lead source's share of the leads created in the window, of those leads that reached a lender, and of those that settled. Colour is fixed per source across this tab.">
      <div ref={barsRef} className="space-y-4" onMouseLeave={() => setHover(null)}>
        {points.map(point => {
          const total = rows.reduce((sum, r) => sum + Math.max(0, point.of(r)), 0);
          const rowHovered = hover?.point === point.label;
          return (
            <div key={point.label} data-testid={`mix-${point.label}`}>
              <div className="mb-1 flex items-baseline justify-between text-xs text-gray-400">
                <b className="font-semibold text-gray-200">{point.label}</b>
                <span className="tabular-nums">{totalText(total)}</span>
              </div>
              {total === 0 ? (
                <p className="text-xs text-gray-500">None in this selection.</p>
              ) : (
                <div className="flex h-4 overflow-hidden rounded-full bg-gray-900/70" onMouseLeave={() => setHover(null)}>
                  {rows.filter(r => point.of(r) > 0).map(r => {
                    const value = point.of(r);
                    const label = `${r.leadSource}: ${shareOf(value, total)} (${fmt(value)})`;
                    const active = rowHovered && hover?.key === r.leadSource;
                    const show = (el: Element) => setHover({ point: point.label, key: r.leadSource, anchor: anchorOf(el) });
                    return <div key={r.leadSource} aria-label={label} tabIndex={0}
                      aria-describedby={active ? tooltipId : undefined}
                      onMouseEnter={e => show(e.currentTarget)}
                      onFocus={e => show(e.currentTarget)}
                      onBlur={() => setHover(null)}
                      onKeyDown={e => { if (e.key === 'Escape') setHover(null); }}
                      className="outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
                      style={{
                        width: `${(value / total) * 100}%`,
                        background: segmentFill(colours.get(r.leadSource) ?? NOT_RECORDED_COLOUR),
                        opacity: rowHovered && !active ? 0.45 : 1,
                      }} />;
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Legend names={rows.map(r => r.leadSource)} colours={colours} />
      <FloatingTooltip anchor={hover && hovered ? hover.anchor : null} id={tooltipId} onDismiss={() => setHover(null)} within={barsRef}>
        {hover && hovered && <BreakdownCard title={hovered.label} rows={breakdown} activeKey={hover.key} total={totalText(hoveredTotal)} />}
      </FloatingTooltip>
    </Panel>
  );
};

/**
 * Attainment of each row's entry cohort. The copy says so twice, because the tempting misreading
 * ("settled of lodged" as a lender win rate) is wrong: both figures come from leads CREATED in the
 * window, so recent cohorts read low simply because they have had less time.
 */
export const ConversionPanel: React.FC<{
  rows: ConversionRow[] | undefined;
  dimension: 'partners' | 'source';
}> = ({ rows, dimension }) => {
  const title = dimension === 'partners' ? 'Conversion by partner' : 'Conversion by source';
  return (
    <Panel title={title} icon={Percent} accent={chartColour(2)}
      blurb="Attainment on an entry cohort, not a funnel: of the leads created in the window, how many reached a lender and how many settled. A short or recent window has had less time to settle, so it reads low.">
      {!rows ? (
        <Honesty>Choose a Created during window to see conversion. A whole snapshot mixes deals of every age, so no ratio from it is a conversion rate.</Honesty>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">No leads in this selection.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label={title}>
            <thead className="text-xs uppercase tracking-wider text-gray-400"><tr>
              <th className="px-3 py-2">{dimension === 'partners' ? 'Partner' : 'Lead Source'}</th>
              <th className="px-3 py-2 text-right">Leads</th>
              <th className="px-3 py-2 text-right">Lodged of leads</th>
              <th className="px-3 py-2 text-right">Settled of lodged</th>
              <th className="px-3 py-2 text-right">Settled of leads</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-700/60">
              {rows.map(row => (
                <tr key={row.key} className="text-gray-300">
                  <td className="px-3 py-2">{row.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(row.leads)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{bpPercent(row.lodgedPerLeadsBp)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{bpPercent(row.settledPerLodgedBp)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{bpPercent(row.settledPerLeadsBp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

const monthLabel = (key: string): string => {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

/** Twelve stacked columns, one per month, split by source. */
export const SettlementsByMonthPanel: React.FC<{ data: SettlementsByMonth; colours: Map<string, string> }> = ({ data, colours }) => {
  const [mode, setMode] = useState<Mode>('value');
  const [hover, setHover] = useState<{ index: number; key: string | null; anchor: TooltipAnchor } | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const pick = (s: { counts: number[]; valueCents: number[] }) => (mode === 'count' ? s.counts : s.valueCents);
  const totals = pick(data.totals);
  const max = Math.max(0, ...totals);
  const fmt = (v: number) => (mode === 'count' ? num(v) : money(v));
  // The card sits beside the hovered column, centred on the chart's height.
  const showColumn = (index: number, key: string | null, column: Element) =>
    setHover({ index, key, anchor: anchorOf(column, chartRef.current) });
  const breakdown: BreakdownRow[] = hover
    ? data.series.filter(s => pick(s)[hover.index] > 0).map(s => ({
      key: s.leadSource,
      name: s.leadSource,
      colour: colours.get(s.leadSource) ?? NOT_RECORDED_COLOUR,
      value: fmt(pick(s)[hover.index]),
      share: shareOf(pick(s)[hover.index], totals[hover.index]),
    }))
    : [];
  return (
    <Panel title="Settlements by month, by source" icon={CalendarRange} accent={chartColour(5)} badge={<ModeToggle mode={mode} onChange={setMode} label="Settlements measure" />}
      blurb="Deals for the chosen broker that settled in each of the twelve months to the snapshot, dated as Deal movements dates them: the Settled column in the Performance export, or the Current Status Date of a Settled deal where the export has no Settled column. Not limited to the Created during window: a settlement belongs to the month it settled.">
      {max === 0 ? (
        <p className="text-sm text-gray-400">No dated settlements in these twelve months.</p>
      ) : (
        <div ref={chartRef} className="flex h-48 items-end gap-1.5" role="group" aria-label="Settlements by month, by source" onMouseLeave={() => setHover(null)}>
          {data.months.map((month, i) => {
            const columnHovered = hover?.index === i;
            return (
              <div key={month} data-testid={`settled-month-${month}`} className="flex h-full flex-1 flex-col items-center justify-end gap-1"
                onMouseEnter={e => { if (totals[i] > 0) showColumn(i, null, e.currentTarget); }}
                onMouseLeave={() => setHover(null)}>
                <span className="text-[0.625rem] tabular-nums text-gray-400">{totals[i] > 0 ? fmt(totals[i]) : ''}</span>
                <div className="flex w-full flex-col-reverse overflow-hidden rounded-t" style={{ height: `${(totals[i] / max) * 80}%` }}>
                  {data.series.filter(s => pick(s)[i] > 0).map(s => {
                    const active = columnHovered && hover?.key === s.leadSource;
                    const column = (el: Element) => el.closest('[data-testid^="settled-month-"]') ?? el;
                    return <div key={s.leadSource} tabIndex={0}
                      aria-label={`${s.leadSource}, ${monthLabel(month)}: ${fmt(pick(s)[i])} (${shareOf(pick(s)[i], totals[i])})`}
                      aria-describedby={active ? tooltipId : undefined}
                      onMouseEnter={e => showColumn(i, s.leadSource, column(e.currentTarget))}
                      onMouseLeave={() => setHover(h => (h && h.index === i ? { ...h, key: null } : h))}
                      onFocus={e => showColumn(i, s.leadSource, column(e.currentTarget))}
                      onBlur={() => setHover(null)}
                      onKeyDown={e => { if (e.key === 'Escape') setHover(null); }}
                      className="outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
                      style={{
                        flexGrow: pick(s)[i],
                        background: segmentFill(colours.get(s.leadSource) ?? NOT_RECORDED_COLOUR),
                        opacity: columnHovered && hover?.key && !active ? 0.45 : 1,
                      }} />;
                  })}
                </div>
                <span className="text-[0.625rem] text-gray-500">{monthLabel(month)}</span>
              </div>
            );
          })}
        </div>
      )}
      <FloatingTooltip anchor={hover ? hover.anchor : null} id={tooltipId} onDismiss={() => setHover(null)} within={chartRef}>
        {hover && <BreakdownCard title={monthLabel(data.months[hover.index])} rows={breakdown} activeKey={hover.key} total={fmt(totals[hover.index])} />}
      </FloatingTooltip>
      <Legend names={data.series.map(s => s.leadSource)} colours={colours} />
      <div className="mt-3">
        <Honesty>
          {data.lastMonthPartial && 'The last month runs only to the snapshot date. '}
          {data.undatedSettled > 0
            ? `${num(data.undatedSettled)} settled deals (${money(data.undatedSettledLoanAmountCents)}) carry no settled date and no settled status date, so they cannot be placed in a month and are not in this chart.`
            : 'Every settled deal in scope carries a settled date.'}
        </Honesty>
      </div>
    </Panel>
  );
};

const TREND_TONE = { Growing: 'good', Steady: 'muted', Cooling: 'warn' } as const;

export const ReferrersPanel: React.FC<{ referrers: LeadSourceInsights['referrers'] }> = ({ referrers }) => (
  <Panel title="Top referrers and relationship health" icon={UserCheck} accent={chartColour(1)}
    blurb={`Every deal each partner referred in this Sales export for the chosen broker, whatever the Created during window. Referrals in the last 90 days (${referrers.last90From} to ${referrers.last90To}) against the 90 before (${referrers.prior90From} to ${referrers.prior90To}). Growing or Cooling needs a change of at least two referrals and at least a quarter of the earlier figure; anything smaller is Steady.`}>
    {referrers.rows.length === 0 ? (
      <p className="text-sm text-gray-400">No named partners in this selection.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" aria-label="Top referrers">
          <thead className="text-xs uppercase tracking-wider text-gray-400"><tr>
            <th className="px-3 py-2">Partner</th>
            <th className="px-3 py-2 text-right">Leads</th>
            <th className="px-3 py-2 text-right">Settled</th>
            <th className="px-3 py-2 text-right">Settled $</th>
            <th className="px-3 py-2 text-right">Last referral</th>
            <th className="px-3 py-2 text-right">Last 90 days</th>
            <th className="px-3 py-2 text-right">Prior 90 days</th>
            <th className="px-3 py-2">Trend</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-700/60">
            {referrers.rows.map((row: ReferrerRow) => (
              <tr key={row.key} className="text-gray-300">
                <td className="px-3 py-2">{row.partnerLabel}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(row.leads)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(row.settled)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(row.settledLoanAmountCents)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{row.lastReferralDate ?? '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(row.last90)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{num(row.prior90)}</td>
                <td className="px-3 py-2"><Pill tone={TREND_TONE[row.trend]}>{row.trend}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </Panel>
);
